import React, { useMemo } from 'react';
import { Buttons, FormGrid, JsonEditorTextarea, OccupyFreeSpace, SimpleDialog, useCopyText } from '../../primitives';
import { FieldDefinition, FormControlsForFields } from '../../schemed';
import { RecordEditData } from './useRecordEdit';
import { LinkedRecordSelectorMultiple } from './LinkedRecordSelector';
import { AttributeConfig } from '../useRecordTypeConfig';
import { TagSelector } from './TagSelector';
import { FormattedMessage } from 'react-intl';
import { Button, Divider, FormHelperText, Typography } from '@mui/material';
import { Settings } from '@mui/icons-material';
import { useEditItem2 } from '../../../api/useNewItem';
import { getEntityAttributesFormSettingsKey, useCMSSettingsContext } from '../Configuration/CMSSettingsContext';

interface Props {
    data: RecordEditData;
}

const RightColumnFieldTypes = ["mediafile", "mediafile_multi", "json", "textlong", "text-long", "markdown", "powerdoc"];
const FancyFieldTypes = ["tag", "tag_multiple", "record_link", "record_link_multiple"];



interface AttributeProps {
  attribute: AttributeConfig;
  data: RecordEditData;
}

const AttributeControl = (props: AttributeProps) => {
  const { attribute: a } = props;
  const { data, updateAttributes } = props.data;
  const { attributesSchema, } = props.data.config;

  if(FancyFieldTypes.includes(a.fieldtype || "no")) {
    switch(a.fieldtype) {
      case "record_link":
      case "record_link_multiple":
        return (
          <LinkedRecordSelectorMultiple
            key={a.code}
            config={a}
            data={data.attributes || {}}
            onChange={(o,c) => updateAttributes(c)}
            />
        );
      case "tag":
      case "tag_multiple":
        return (
          <TagSelector
            key={a.code}
            config={a}
            data={data.attributes || {}}
            onChange={(o,c) => updateAttributes(c)}
            />
        )
      default:
        return null;
    }
  }

  return (
    <FormControlsForFields
      data={data.attributes || {}}
      onChange={(o,c) => updateAttributes(c)}
      schema={attributesSchema}
      fields={[[a.code]]}
      />
  )

}



interface AutoFormProps extends Props {
  excludeFields?: string[];
}

export const RecordAttributesFormAuto = (props: AutoFormProps) => {
    const { data, updateAttributes } = props.data;
    const { data: config, attributesSchema, } = props.data.config;

    const { fat, normal, fancyConfigs } = useMemo(
        () => (config.attributes || []).reduce<{ normal: FieldDefinition[], fat: FieldDefinition[], fancyConfigs: AttributeConfig[] }>(
            (r,a) => {
                if(props.excludeFields?.includes(a.code)) {
                  return r;
                }
                if(RightColumnFieldTypes.includes(a.fieldtype || "")) {
                    r.fat.push([a.code]);
                } else if(FancyFieldTypes.includes(a.fieldtype || "")) {
                    r.fancyConfigs.push(a);
                } else {
                    r.normal.push([a.code]);
                }
                return r;
            }, { normal: [], fat: [], fancyConfigs: [] }),
            [config.attributes, props.excludeFields]
        );

        const normals = normal.length ? 1 : 0;
        const fancies = fancyConfigs.length ? 1 : 0;
        const fats = fat.length ? 1 : 0;

        const variants = normals + fancies + fats;
    
        return (
          <FormGrid columns={variants === 2 ? (normals && normal.length > 1 ? "2fr 1fr" : "1fr 1fr") : `repeat(${variants}, 1fr)`} forceEvenColumns>
            {!!normals &&
              <FormGrid columns={variants === 1 ? "1fr 1fr 1fr" : variants === 2 && normal.length > 1 ? "1fr 1fr" : "1fr"} noMargin forceEvenColumns>
                <FormControlsForFields
                    data={data.attributes || {}}
                    onChange={(o,c) => updateAttributes(c)}
                    schema={attributesSchema}
                    fields={normal}
                    />
              </FormGrid>}

            {!!fancies &&
              <FormGrid columns={variants === 1 ? "1fr 1fr 1fr" : "1fr"} noMargin forceEvenColumns>
                {fancyConfigs.map(f => {
                  switch(f.fieldtype) {
                    case "record_link":
                    case "record_link_multiple":
                      return (
                        <LinkedRecordSelectorMultiple
                          key={f.code}
                          config={f}
                          data={data.attributes || {}}
                          onChange={(o,c) => updateAttributes(c)}
                          />
                      );
                    case "tag":
                    case "tag_multiple":
                      return (
                        <TagSelector
                          key={f.code}
                          config={f}
                          data={data.attributes || {}}
                          onChange={(o,c) => updateAttributes(c)}
                          />
                      )
                    default:
                      return null;
                  }
                })}
              </FormGrid>}

            {!!fats &&
              <FormGrid columns={variants === 1 ? "1fr 1fr 1fr" : "1fr"} key={data._id} noMargin>
                <FormControlsForFields
                    data={data.attributes || {}}
                    onChange={(o,c) => updateAttributes(c)}
                    schema={attributesSchema}
                    fields={fat}
                    />
              </FormGrid>}
            
        </FormGrid>)
}


interface IFormSection {
  cols?: string;
  attributes: string[];
  title?: string;
  comment?: string;
}

export const RecordAttributesForm = (props: Props) => {
    const { data } = props.data;
    const { data: config, } = props.data.config;

    const settingsCtx = useCMSSettingsContext();
    const copyText = useCopyText();

    const currentFormSettings: { sections: IFormSection[] } | undefined = settingsCtx.settings[getEntityAttributesFormSettingsKey(data.record_type)];

    const fieldsFromConfiguredSections = useMemo(() => {
      if(!currentFormSettings || !currentFormSettings.sections) {
        return [];
      }
      return currentFormSettings.sections.reduce<string[]>((r,s) => { return [...r, ...(s.attributes || [])]; }, [])
    }, [currentFormSettings]);

    const editFormSettings = useEditItem2<{ config: string }>({
      save: async (item) => {
        try {
          const parsed = JSON.parse(item.config);
          settingsCtx.updateSettings({ [getEntityAttributesFormSettingsKey(data.record_type)]: parsed });
        } catch(e) {
        }
        return item;
      }
    })

    const startEditFormSettings = () => {
      const current = currentFormSettings
        || {
          sections: [
            {
              title: "",
              comment: "",
              cols: "",
              attributes: [],
            }
          ]
        };
      editFormSettings.startEditing({
        config: JSON.stringify(current, null, 2),
      });
    }

    return <>
      <Buttons>
        <OccupyFreeSpace />
        <Button size="small" color="primary" startIcon={<Settings />} onClick={startEditFormSettings} >
          <FormattedMessage id="common.configure" />
        </Button>
      </Buttons>

      {currentFormSettings?.sections.map(s => (
        <FormGrid columns="1fr" gap="dense">
          <div style={{ width: "100%" }}>
            {!!s.title && <Typography style={{ margin: 0 }}>{s.title}</Typography>}
            {!!s.comment && <FormHelperText style={{ whiteSpace: "pre-line" }}>{s.comment}</FormHelperText>}
            <Divider />
          </div>

          {!!s.attributes?.length &&
            <FormGrid noMargin columns={s.cols || "repeat(3,1fr)"} forceEvenColumns>
              {s.attributes.map(a => config.attributes?.find(x => x.code === a))
                .map((a,idx) => a
                  ? (
                    <AttributeControl
                      key={a.code}
                      attribute={a as any}
                      data={props.data}
                      />
                  )
                  : <div key={`placeholder${idx}`} className="placeholder" />)}
            </FormGrid>}
        </FormGrid>
      ))}
      
      {!!fieldsFromConfiguredSections.length &&
        <Divider style={{ marginTop: "1rem" }} />}

      <RecordAttributesFormAuto
        data={props.data}
        excludeFields={fieldsFromConfiguredSections}
        />



      {!!editFormSettings.item &&
        <SimpleDialog
          noFullscreen
          submitOnModEnter
          maxWidth="lg"
          fullWidth
          {...editFormSettings.dialog}
          dialogTitle={<FormattedMessage id="common.configure" />}
          save={() => editFormSettings.save()}>
            
            <FormGrid columns="1fr" noMargin>
              {!!config.attributes?.length &&
                <Buttons style={{ flexFlow: "row wrap", rowGap: "0"}}>
                  {config.attributes.map(a => (
                    <Typography variant="caption" color="primary" style={{ cursor: "pointer" }} onClick={() => copyText(a.code)}>
                      {a.label} ({a.code})
                    </Typography>
                  ))}
                </Buttons>}
              <JsonEditorTextarea
                value={editFormSettings.item.config}
                onChange={e => editFormSettings.update({ config: e.target.value })}
                />
            </FormGrid>
        </SimpleDialog>}
    </>
}
